/****************
 * THE SPEED RUN SEES NONE OF THE REAL ACCOUNT (Guillaume 11-01: "He still sees
 * his own open trades and data: the speed run must show none of his real
 * account").
 *
 * Applied once, in the request function of each app (web fetchApi, phone
 * request: api/client.ts), to every account answer while a
 * run is on: only what was made DURING the run shows (its sessions, their
 * orders and positions, a key added in the run). Everything else reads as a
 * new account: no trades, no wallet, no recap, no messages, no Orb nudge or
 * profile. The data itself is untouched; leaving the run shows it again.
 * Sessions and models are already filtered where they are read
 * (hooks/useSessions.ts, hooks/useModels.ts).
 ****************/
import { madeDuringRun, speedRunStartedAt } from '@/lib/speedRun';

type Get = (endpoint: string) => Promise<any>;

const FAV_KEY = 'copyfi.speedRun.favorites';
/** The traders starred during the run (favoritesApi.add records them). */
export function runFavorites(): string[] {
  try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]') as string[]; } catch { return []; }
}
export function rememberRunFavorite(uid: string): void {
  try { localStorage.setItem(FAV_KEY, JSON.stringify([...new Set([...runFavorites(), uid])])); } catch { /* private mode */ }
}

let idsCache: { at: number; ids: Promise<Set<number>> } | null = null;
let keysCache: { at: number; has: Promise<boolean> } | null = null;
/** Tests: forget the cached run sessions and keys. */
export function resetRunLensCache(): void { idsCache = null; keysCache = null; }

/** The sessions made during the run. Read raw (no lens) and kept 10 s. */
function runSessionIds(get: Get): Promise<Set<number>> {
  if (idsCache && Date.now() - idsCache.at < 10_000) return idsCache.ids;
  const ids = get('/api/v1/replicator/subscriptions')
    .then((subs: any[]) => new Set((subs || []).filter((s) => madeDuringRun(s.created_at)).map((s) => Number(s.id))))
    .catch(() => new Set<number>());
  idsCache = { at: Date.now(), ids };
  return ids;
}

/** Whether an exchange key was added during the run. */
function runHasKey(get: Get): Promise<boolean> {
  if (keysCache && Date.now() - keysCache.at < 10_000) return keysCache.has;
  const has = get('/api/v1/replicator/credentials')
    .then((keys: any[]) => (keys || []).some((k) => madeDuringRun(k.created_at)))
    .catch(() => false);
  keysCache = { at: Date.now(), has };
  return has;
}

const inRun = (ids: Set<number>, id: unknown) => ids.has(Number(id));
const emptySide = { closed: 0, wins: 0, losses: 0, net_pnl: 0, best: null, worst: null };

/** The answer a newcomer would get for this endpoint; unchanged when it is not account data. */
export async function newcomerLens(endpoint: string, data: any, get: Get): Promise<any> {
  const path = endpoint.split('?')[0];
  switch (path) {
    case '/api/v1/replicator/orders':
    case '/api/v1/replicator/positions': {
      if (!Array.isArray(data)) return data;
      const ids = await runSessionIds(get);
      return data.filter((r) => inRun(ids, r.subscription_id));
    }
    case '/api/v1/replicator/open-positions': {
      if (!data || typeof data !== 'object') return data;
      const ids = await runSessionIds(get);
      const positions = (data.positions || []).filter((p: any) => (p.sessions || []).some((s: any) => inRun(ids, s.subscription_id)));
      return {
        ...data,
        positions,
        count: positions.length,
        unrealized_pnl: positions.reduce((a: number, p: any) => a + (Number(p.unrealized_pnl) || 0), 0),
        recently_closed: (data.recently_closed || []).filter((t: any) => inRun(ids, t.subscription_id)),
        waiting_at_the_broker: (data.waiting_at_the_broker || []).filter((o: any) => inRun(ids, o.subscription_id)),
      };
    }
    case '/api/v1/replicator/subscriptions':
      // Only the sessions the run started (11-02 audit: "My sessions (5)" counted his real ones).
      return Array.isArray(data) ? data.filter((s) => madeDuringRun(s?.created_at)) : data;
    case '/api/v1/admin/backtests':
    case '/api/v1/backtests': {
      // Only the run's backtests (11-02 audit: his earlier ones were listed).
      const rows = Array.isArray(data) ? data : data?.backtests;
      if (!Array.isArray(rows)) return data;
      const mine = rows.filter((b: any) => madeDuringRun(b?.created_at));
      return Array.isArray(data) ? mine : { ...data, backtests: mine };
    }
    case '/api/v1/replicator/credentials':
      return Array.isArray(data) ? data.filter((k) => madeDuringRun(k.created_at)) : data;
    case '/api/v1/replicator/equity':
      // A new wallet, built fresh: nothing of the real answer is carried (11-02 audit: a key
      // scope's open_book and open_positions came through and showed his open trades).
      return (await runHasKey(get)) ? data
        : { scope: data?.scope, window_hours: data?.window_hours, points: [], current: 0, change_available: false,
            change_abs: 0, change_pct: 0, capital_added: 0, open_positions: 0, open_book: [] };
    case '/api/v1/replicator/history-exposure':
      return (await runHasKey(get)) ? data : { trade_count: 0 };
    case '/api/v1/me/recap':
      return data && typeof data === 'object' ? { ...data, opened: 0, open_now: 0, paper: emptySide, live: emptySide, traders: [], trades: [] } : data;
    case '/api/v1/favorites': {
      // A plain list of trader ids, no dates: only the ones starred during the run.
      const mine = runFavorites();
      return Array.isArray(data) ? data.filter((f) => mine.includes(String(f))) : data;
    }
    case '/api/v1/community/chat':
      // No conversations, and the Messages dock stays hidden as for someone not in Community yet.
      return data && typeof data === 'object' ? { ...data, member: false, chats: [] } : data;
    case '/api/v1/assistant/nudge':
      return { nudge: null };
    case '/api/v1/assistant/profile':
      return data && typeof data === 'object' ? { ...data, profile: null, offer_checkin: false } : data;
    default:
      return data;
  }
}

/****************
 * WHAT THE RUN MADE IS THROWN AWAY WHEN IT ENDS (Guillaume 11-02: "they should
 * be temporary saves that auto delete when we exit the speed run").
 * Leaving the run deletes what the run itself created: its strategies,
 * backtests, paper sessions, and the traders starred in it that were not
 * favourites before. A LIVE session or an exchange key is real money and is kept.
 *
 * ONLY WHAT IT RECORDED, NEVER BY DATE (11-03 incident): the first version
 * deleted every row created after the run's start, and a missing start read as
 * "everything", so leaving a run on /m deleted every backtest of the account
 * (restored from backup), and a run on the web deleted a backtest another
 * session made while it was on. Now each id is written down when the run's own
 * request creates it (recordRunCreation, called by both apps' request
 * function), the cleanup deletes those ids and nothing else, and with no run
 * start it deletes nothing at all.
 ****************/
const RUN_MADE_KEY = 'copyfi.speedRun.made';
interface RunMade { backtests: number[]; strategies: number[]; sessions: number[] }
function runMade(): RunMade {
  try {
    const m = JSON.parse(localStorage.getItem(RUN_MADE_KEY) || '{}') as Partial<RunMade>;
    return { backtests: m.backtests ?? [], strategies: m.strategies ?? [], sessions: m.sessions ?? [] };
  } catch { return { backtests: [], strategies: [], sessions: [] }; }
}

/** What a POST created during the run, by its endpoint: the cleanup's list. No run, nothing kept. */
export function recordRunCreation(method: string | undefined, endpoint: string, body: unknown): void {
  if (!speedRunStartedAt() || (method || 'GET').toUpperCase() !== 'POST') return;
  const path = endpoint.split('?')[0];
  const b = (body ?? {}) as Record<string, any>;
  const id = Number(b.id ?? b.subscription?.id ?? b.subscription_id ?? b.backtest?.id ?? 0);
  if (!(id > 0)) return;
  let kind: keyof RunMade | null = null;
  if (/^\/api\/v1\/(admin\/)?backtests(\/\d+\/rerun|\/from-session\/\d+)?$/.test(path)) kind = 'backtests';
  else if (path === '/api/v1/models') kind = 'strategies';
  else if (path === '/api/v1/replicator/subscriptions') kind = 'sessions';
  if (!kind) return;
  try {
    const m = runMade();
    if (!m[kind].includes(id)) m[kind].push(id);
    localStorage.setItem(RUN_MADE_KEY, JSON.stringify(m));
  } catch { /* private mode: nothing recorded, nothing deleted */ }
}
const FAV_BEFORE_KEY = 'copyfi.speedRun.favoritesBefore';
/** Kept when the run starts: the favourites the account already had. */
export function rememberFavoritesBefore(uids: string[]): void {
  try { localStorage.setItem(FAV_BEFORE_KEY, JSON.stringify(uids.map(String))); } catch { /* private mode */ }
}
function favoritesBefore(): string[] | null {
  try { const raw = localStorage.getItem(FAV_BEFORE_KEY); return raw ? (JSON.parse(raw) as string[]) : null; } catch { return null; }
}

export interface RunCleanupApi { get: (endpoint: string) => Promise<any>; del: (endpoint: string) => Promise<any> }
export interface RunCleanup { deleted: string[]; kept: string[]; failed: string[] }

export async function cleanUpRun(api: RunCleanupApi): Promise<RunCleanup> {
  const out: RunCleanup = { deleted: [], kept: [], failed: [] };
  // No run start: this is not a run, so nothing is anyone's to delete.
  if (!speedRunStartedAt()) { out.kept.push('no run start: nothing deleted'); return out; }
  const drop = async (label: string, endpoint: string) => {
    try { await api.del(endpoint); out.deleted.push(label); } catch { out.failed.push(label); }
  };
  const made = runMade();
  // Sessions first: a paper session the run started is deleted; a live one is kept (real money).
  if (made.sessions.length > 0) {
    let rows: any[] = [];
    try { const d = await api.get('/api/v1/replicator/subscriptions'); rows = Array.isArray(d) ? d : []; } catch { rows = []; }
    for (const id of made.sessions) {
      const s = rows.find((x) => Number(x.id) === id);
      if (!s) { out.kept.push(`session ${id} (not found)`); continue; }
      const paper = s.is_paper === true || String(s.session_type || s.mode || '').toLowerCase() === 'paper';
      if (paper) await drop(`session ${id}`, `/api/v1/replicator/subscriptions/${id}`);
      else out.kept.push(`live session ${id}`);
    }
  }
  for (const id of made.backtests) await drop(`backtest ${id}`, `/api/v1/backtests/${id}`);
  for (const id of made.strategies) await drop(`strategy ${id}`, `/api/v1/models/${id}`);
  const before = favoritesBefore();
  if (before) {
    for (const uid of runFavorites().filter((u) => !before.includes(String(u)))) {
      await drop(`favourite ${uid}`, `/api/v1/favorites?trader_uid=${encodeURIComponent(uid)}`);
    }
  }
  return out;
}
